import { useStore } from '../composables/store'
import { useCheckoutStore } from '../stores/store'
import { nextTick } from 'vue'
import axios from 'axios'
import { cloneDeep } from 'lodash'
import { captureValidationError, CheckoutAction } from './sentry'

// Initialize store lazily when functions are called rather than at import time
const getStore = () => useStore()
const getCheckout = () => useCheckoutStore()

/**
 * Validates inventory levels and variants for items in the cart
 * Also fetches BC pricing and checkout totals in one consolidated backend call
 * @returns {Object} Object containing:
 *   - oosItems: Array of out of stock items removed from cart
 *   - lowStockItems: Array of items with reduced quantities
 *   - invalidItems: Array of invalid variant combinations removed
 *   - validationFailed: Boolean indicating if validation encountered errors (API failures, etc.)
 */
export async function validateInventory() {
	const store = getStore()
	const checkout = getCheckout()
	const cart = store.cart

	console.log('🔍 validateInventory - Starting cart validation')

	// Skip validation if cart is empty
	if (!cart?.cartItems?.length) {
		console.log('⚠️ Cart is empty, skipping validation')
		return { oosItems: [], lowStockItems: [], invalidItems: [], validationFailed: false }
	}

	// Set validation state to true to disable cartItems watcher
	checkout.updateCartValidation(true)

	let response
	try {
		console.log('📡 Calling consolidated validation endpoint...')
		response = await axios.post(`${store.api}/controllers/carts/validate`, {
			cartItems: cart.cartItems,
			bcCartId: cart.bcId,
		})
	} catch (error) {
		console.error('❌ Error calling validation endpoint:', error)
		captureValidationError(error, {
			action: CheckoutAction.VALIDATE_CART,
			store,
			cartItemsCount: cart.cartItems.length,
		})
		checkout.updateCartValidation(false)
		return { oosItems: [], lowStockItems: [], invalidItems: [], validationFailed: true }
	}

	const { oosItems = [], lowStockItems = [], invalidVariants = [], validationFailed = false, enrichedCart, checkout: checkoutTotals } = response.data

	// Apply cart changes if there are items to remove or update
	const needsBcResync = oosItems.length > 0 || lowStockItems.length > 0 || invalidVariants.length > 0
	const hasChanges = needsBcResync

	console.log('[validateInventory] hasChanges:', hasChanges, 'enrichedCart:', !!enrichedCart, 'validatingCart:', checkout?.validatingCart)

	if (hasChanges || enrichedCart) {
		console.log('[validateInventory] Applying enriched cart. cartItems before:', cart.cartItems?.length, 'enrichedCart items:', enrichedCart?.cartItems?.length)
		// Apply enriched cart data (includes filtered items and pricing)
		// validatingCart stays true during mutations so the cartItems watcher doesn't trigger saveCart
		if (enrichedCart) {
			cart.cartItems = enrichedCart.cartItems
			cart.baseAmount = enrichedCart.baseAmount
			cart.discountAmount = enrichedCart.discountAmount
			cart.taxIncluded = enrichedCart.taxIncluded
			cart.manualDiscountAmount = enrichedCart.manualDiscountAmount
			cart.cartAmountIncTax = enrichedCart.cartAmountIncTax
			cart.cartAmountExTax = enrichedCart.cartAmountExTax
		}

		// Apply checkout totals
		if (checkoutTotals) {
			checkout.updateTaxes(checkoutTotals.taxes)
			cart.subTotal = checkoutTotals.subTotal
			cart.tax = checkoutTotals.taxTotal
			cart.total = checkoutTotals.grandTotal - checkoutTotals.shippingCostTotal
		}

		// Sync originalCart so the cartItems watcher's diff doesn't see
		// enrichment or OOS removal as a change that needs saving
		console.log('[validateInventory] Syncing originalCart. validatingCart before sync:', checkout?.validatingCart)
		store.originalCart = cloneDeep(cart)
		console.log('[validateInventory] originalCart synced. cartItems count:', cart.cartItems?.length)

		// Update local storage
		checkout.saveCartToLocalStorage(store.cart)
	}

	// Wait for Vue to flush watchers (which will see validatingCart=true and skip)
	// before releasing the guard, so the cartItems watcher doesn't trigger saveCart
	console.log('[validateInventory] Awaiting nextTick before releasing validatingCart guard')
	await nextTick()
	console.log('[validateInventory] nextTick complete, releasing validatingCart guard')
	checkout.updateCartValidation(false)

	// After OOS/low-stock/invalid removals, explicitly rebuild the BC cart so create_order
	// cannot still see lines we removed only from the local cart.
	if (needsBcResync && typeof store.saveCart === 'function') {
		console.log('[validateInventory] Rebuilding BC cart after inventory changes')
		await store.saveCart()
	} else if ((cart.cartItems?.length > 0 || cart.certificates?.length > 0) && store.user?.id && !cart?.id) {
		// Logged-in user with cart items, triggering saveCart to persist
		console.log('[validateInventory] Logged-in user with cart items, triggering saveCart to persist')
		Promise.resolve(store.saveCart()).catch((error) => {
			console.error('[validateInventory] saveCart failed:', error)
		})
	}

	// Log validation results
	console.log('\n📋 Validation Results Summary:')
	console.log(`🚫 Out of stock items removed: ${oosItems.length}`)
	console.log(`⚠️ Low stock items updated: ${lowStockItems.length}`)
	console.log(`🚫 Invalid variants removed: ${invalidVariants.length}`)
	console.log('🛒 Final cart items count:', cart.cartItems.length)

	return { oosItems, lowStockItems, invalidItems: invalidVariants, validationFailed }
}
